import { ArrowRight, Loader2, Compass, MessageSquare, MessageCircle, FilePlus2 } from "lucide-react";
import CvSourceTabs from "./CvSourceTabs";
import { stepLabel } from "../studio/constants";
import { firstName } from "../../lib/utils";

const greet = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; };

function NextAction({ baseCv, processing, resume, onGo, onOpenApp, onCvChange }) {
  const Card = ({ eyebrow, title, body, children }) => (
    <div className="gi-card p-6 sm:p-8 border-brand/25" data-testid="home-next-action">
      <div className="gi-eyebrow">{eyebrow}</div>
      <h2 className="font-display text-xl sm:text-2xl font-bold text-ink mt-2">{title}</h2>
      <p className="text-[15px] text-body mt-1.5 max-w-xl">{body}</p>
      <div className="mt-5">{children}</div>
    </div>
  );
  if (!baseCv) return (
    <Card eyebrow="Your first step" title="Let's start with your CV" body="Upload the CV you already have, or create one from your LinkedIn profile. You only do this once, and every application starts from it.">
      <div className="max-w-2xl"><CvSourceTabs compact testid="home-cv-source" onDone={async () => { await onCvChange(); onGo("studio"); }} /></div>
    </Card>
  );
  if (processing) return (
    <Card eyebrow="Working on it" title={`Tailoring your CV${processing.role ? ` for ${processing.role}` : ""}`} body="This takes about a minute. We'll keep going even if you leave.">
      <button data-testid="home-open-processing" onClick={() => onOpenApp(processing.id)} className="gi-btn gi-btn-primary"><Loader2 className="w-4 h-4 animate-spin" /> See progress</button>
    </Card>
  );
  if (resume) return (
    <Card eyebrow="Pick up where you left off" title={`${resume.role || "Application"}${resume.company ? ` at ${resume.company}` : ""}`} body={`Next: step ${resume.journey_step || 3} of 6, ${stepLabel(resume.journey_step || 3).toLowerCase()}.`}>
      <div className="flex flex-wrap gap-2">
        <button data-testid="home-resume-button" onClick={() => onOpenApp(resume.id)} className="gi-btn gi-btn-primary">Continue <ArrowRight className="w-4 h-4" /></button>
        <button data-testid="home-new-button" onClick={() => onOpenApp(null)} className="gi-btn gi-btn-secondary">Start a different job</button>
      </div>
    </Card>
  );
  return (
    <Card eyebrow="Ready when you are" title="Tailor your CV for your next job" body="Paste a job ad and we'll show how well you fit, then tailor your real experience to it in about a minute.">
      <button data-testid="home-new-button" onClick={() => onOpenApp(null)} className="gi-btn gi-btn-primary"><FilePlus2 className="w-4 h-4" /> Start a new application</button>
    </Card>
  );
}

export default function Home({ user, baseCv, applications, onGo, onOpenApp, onCvChange }) {
  const done = applications.filter((a) => a.processing_status === "done");
  const processing = applications.find((a) => a.processing_status === "processing");
  const unfinished = done.filter((a) => a.status === "tailored" && (a.journey_step || 3) < 6);
  const count = (s) => done.filter((a) => a.status === s).length;
  const first = firstName(user);
  return (
    <div className="af-fade-up space-y-6" data-testid="home-view">
      <div>
        <h1 data-testid="home-greeting" className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">{greet()}{first ? `, ${first}` : ""}</h1>
        <p className="text-body mt-1">{done.length ? "Here's where things stand." : "Welcome to GetInterviewed. Here's how to get your first tailored CV."}</p>
      </div>
      <NextAction baseCv={baseCv} processing={processing} resume={unfinished[0]} onGo={onGo} onOpenApp={onOpenApp} onCvChange={onCvChange} />
      {done.length > 0 && (
        <div className="grid grid-cols-3 gap-3" data-testid="home-stats">
          {[["Applications", done.length], ["Interviews", count("interview")], ["Offers", count("offer")]].map(([l, v]) => (
            <button key={l} onClick={() => onGo("tracker")} className="gi-card p-4 text-left hover:border-brand/40 transition-colors">
              <div className="font-display text-2xl font-bold text-ink">{v}</div><div className="text-sm text-body">{l}</div>
            </button>
          ))}
        </div>
      )}
      {unfinished.length > 1 && (
        <div className="gi-card p-5 sm:p-6" data-testid="home-in-progress">
          <h3 className="font-display text-lg font-bold text-ink mb-3">Also in progress</h3>
          <ul className="divide-y divide-line">
            {unfinished.slice(1, 4).map((a) => (
              <li key={a.id} className="py-3 flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-ink truncate">{a.role || "Application"}{a.company ? ` · ${a.company}` : ""}</div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-subtle max-w-[220px]"><div className="h-full rounded-full bg-brand" style={{ width: `${((a.journey_step || 3) / 6) * 100}%` }} /></div>
                </div>
                <button onClick={() => onOpenApp(a.id)} className="gi-btn gi-btn-secondary gi-btn-sm" data-testid={`home-resume-${a.id}`}>Continue</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-3">
        <button onClick={() => onGo("career")} data-testid="home-link-career" className="gi-card p-5 text-left hover:border-brand/40 transition-colors">
          <Compass className="w-5 h-5 text-brand" /><div className="font-semibold text-ink mt-2">Career Journey</div><div className="text-sm text-body">Guided coaching for your next move</div>
        </button>
        <button onClick={() => onGo("community")} data-testid="home-link-community" className="gi-card p-5 text-left hover:border-brand/40 transition-colors">
          <MessageSquare className="w-5 h-5 text-brand" /><div className="font-semibold text-ink mt-2">Community</div><div className="text-sm text-body">Tips and wins from other job seekers</div>
        </button>
        <button onClick={() => window.dispatchEvent(new Event("kesh-open"))} data-testid="home-link-kesh" className="gi-card p-5 text-left hover:border-kesh/40 transition-colors">
          <MessageCircle className="w-5 h-5 text-kesh" /><div className="font-semibold text-ink mt-2">Ask Kesh</div><div className="text-sm text-body">Your AI career companion, anytime</div>
        </button>
      </div>
    </div>
  );
}
